import { useCallback, useEffect } from 'react';

import { observer } from 'mobx-react';

import { analytics } from '../../../../services/analytics';
import { useStores } from '../../../../stores/useStores';
import { STAMP_CARD_TEST_ID_PREFIX, WIDGET_WIDE_WIDTH } from '../../constants';
import type { StampCardWidgetDataType, WidgetSizeType } from '../../types';
import { WidgetWrapper } from '../WidgetWrapper/WidgetWrapper';

import { StampCardSmallWidget } from './StampCardSmallWidget/StampCardSmallWidget';
import { StampCardTimer } from './StampCardTimer/StampCardTimer';
import { StampCardWideWidget } from './StampCardWideWidget/StampCardWideWidget';
import { useStampCardOpenCloseAnimation } from './useStampCardOpenCloseAnimation';
import { useStampCardReveal } from './useStampCardReveal';

interface StampCardWidgetProps {
  sizeType: WidgetSizeType;
  data: StampCardWidgetDataType;
}

function StampCardWidgetComponent(props: StampCardWidgetProps) {
  const { sizeType } = props;

  const { stampCardStore, widgetsStore, uiStore, animationStore } = useStores();
  const { currentStampCard, justStampedIndexes, justUnlockedIndex } = stampCardStore;
  const assets = currentStampCard?.assets;

  const target = currentStampCard?.stampCount ?? 0;
  const stamps = currentStampCard?.stamps ?? [];
  const current = stamps.filter((stamp) => stamp.status === 'COMPLETED').length;

  const onClick = useCallback(() => {
    void analytics.sendStampCardEvent('widget', {
      event_parent: 'features',
      event_category: 'stamp_card',
      event_action: 'click',
      click_location: 'lobby',
    });
    stampCardStore.openStampCardDialog();
  }, [stampCardStore]);

  const {
    isOpening,
    isHoldingWidth,
    isPoppingOut,
    isClosing,
    isRotatingOnClose,
    isBottomRevealing,
    isOpeningRef,
    openWidget,
    closeWidget,
    popDurationMs,
  } = useStampCardOpenCloseAnimation();

  useEffect(() => {
    widgetsStore.setRailOverflowVisible(isPoppingOut);
    return () => widgetsStore.setRailOverflowVisible(false);
  }, [isPoppingOut, widgetsStore]);

  const { revealedJustStampedIndexes, displayedCurrent } = useStampCardReveal({
    justStampedIndexes,
    clearJustStamped: useCallback(() => stampCardStore.setJustStampedIndexes([]), [stampCardStore]),
    isRevealBlocked: uiStore.isDialogOpen || animationStore.animationBlockScreenVisible,
    sizeType,
    current,
    isOpeningRef,
    openWidget,
    closeWidget,
  });

  const timerIncluded = !!currentStampCard?.expiresAt;
  const timer = timerIncluded ? (
    <StampCardTimer dataTestId={`${STAMP_CARD_TEST_ID_PREFIX}-widget-timer`} extended={sizeType === 'wide'} />
  ) : undefined;
  const timerExtended = timerIncluded ? (
    <StampCardTimer extended dataTestId={`${STAMP_CARD_TEST_ID_PREFIX}-widget-timer-extended`} />
  ) : undefined;

  return (
    <WidgetWrapper
      smallWidgetExtended
      animateWidth={sizeType === 'small' && (isOpening || isHoldingWidth || isClosing)}
      dataTestId={STAMP_CARD_TEST_ID_PREFIX}
      overflowVisible={sizeType === 'small' && isPoppingOut}
      sizeType={sizeType}
      widthOverride={sizeType === 'small' && (isOpening || isHoldingWidth) ? WIDGET_WIDE_WIDTH : undefined}
      onClick={onClick}
    >
      {sizeType === 'small' ? (
        <StampCardSmallWidget
          backgroundUrl={assets?.lobbyWidgetNarrowUrl}
          current={displayedCurrent}
          emptyStampAssetUrl={assets?.emptyStampAssetUrl}
          filledStampAssetUrl={assets?.filledStampAssetUrl}
          freeStampAssetUrl={assets?.freeStampAssetUrl}
          isBottomRevealing={isBottomRevealing}
          isClosing={isClosing}
          isOpening={isOpening}
          isPoppingOut={isPoppingOut}
          isRotatingOnClose={isRotatingOnClose}
          justStampedIndexes={revealedJustStampedIndexes}
          justUnlockedIndex={justUnlockedIndex}
          lockedStampAssetUrl={assets?.lockedStampAssetUrl}
          logoAssetUrl={assets?.logoAssetUrl}
          popDurationMs={popDurationMs}
          stamps={stamps}
          target={target}
          timer={timer}
          timerExtended={timerExtended}
          wideBackgroundUrl={assets?.lobbyWidgetWideUrl}
        />
      ) : (
        <StampCardWideWidget
          backgroundUrl={assets?.lobbyWidgetWideUrl}
          emptyStampAssetUrl={assets?.emptyStampAssetUrl}
          filledStampAssetUrl={assets?.filledStampAssetUrl}
          freeStampAssetUrl={assets?.freeStampAssetUrl}
          justStampedIndexes={revealedJustStampedIndexes}
          justUnlockedIndex={justUnlockedIndex}
          lockedStampAssetUrl={assets?.lockedStampAssetUrl}
          logoAssetUrl={assets?.logoAssetUrl}
          stamps={stamps}
          target={target}
          timer={timer}
        />
      )}
    </WidgetWrapper>
  );
}

export const StampCardWidget = observer(StampCardWidgetComponent);
